import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import './ChessGamesView.css';
import logger from '../../utils/logger';
import { useAuthContext } from '../../contexts/AuthContext';

interface Player {
    id: string;
    color: string;
    odId: string | null;
}

interface CompletedGame {
    id: string;
    name: string;
    state: string;
    winnerId: string | null;
    endReason: string;
    completedAt: string;
    moveNumber: number;
    players: Player[];
}

interface ActiveGame {
    id: string;
    name: string;
    state: string;
    players: Player[];
    playerCount: number;
    lastActivity: number;
}

type FilterTab = 'active' | 'completed' | 'my-games';

const GAMES_PER_PAGE = 20;

function ChessGamesView() {
    const { isAuthenticated, user } = useAuthContext();
    const [completedGames, setCompletedGames] = useState<CompletedGame[]>([]);
    const [activeGames, setActiveGames] = useState<ActiveGame[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<FilterTab>('active');
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
    const navigate = useNavigate();

    const navigateToHome = (): void => {
        navigate("/");
    };

    const navigateToLobby = (): void => {
        navigate("/mferchess/");
    };

    // Reset page when tab changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab]);

    useEffect(() => {
        fetchActiveGames();
        fetchGames(currentPage);
    }, [currentPage]);

    // Handle tab change
    const handleTabChange = (tab: FilterTab): void => {
        if (tab !== activeTab) {
            setActiveTab(tab);
        }
    };

    const fetchActiveGames = async (): Promise<void> => {
        try {
            const response = await fetch(`${SERVER_URL}/api/chess/games`);

            if (!response.ok) {
                throw new Error('Failed to fetch active games');
            }

            const data = await response.json();
            logger.debug("Fetched active games", { count: Object.keys(data).length });

            // Convert object to array and filter for only ongoing games
            const gamesArray: ActiveGame[] = Object.entries(data)
                .map(([id, game]: [string, any]) => ({
                    id,
                    name: game.gameName,
                    state: game.state,
                    players: game.players,
                    playerCount: game.players.length,
                    lastActivity: game.lastActivity
                }))
                .filter(game => game.state === 'ongoing');

            setActiveGames(gamesArray);
        } catch (err) {
            logger.error('Error fetching active games:', err);
            // Don't set error state for active games, just log it
        }
    };

    const fetchGames = async (page: number): Promise<void> => {
        setLoading(true);
        setError(null);

        try {
            const offset = (page - 1) * GAMES_PER_PAGE;
            const response = await fetch(
                `${SERVER_URL}/api/chess/games/completed?limit=${GAMES_PER_PAGE}&offset=${offset}`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch completed games');
            }

            const data: CompletedGame[] = await response.json();
            logger.debug("Fetched completed games", { page, count: data.length });

            setCompletedGames(data);
            setHasMore(data.length === GAMES_PER_PAGE);
        } catch (err) {
            logger.error('Error fetching completed games:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    // Filter games based on active tab
    const filteredGames = useMemo(() => {
        if (activeTab === 'active') {
            return activeGames;
        }
        if (activeTab === 'completed') {
            return completedGames;
        }
        // "My Games" tab - filter for games where user participated
        if (isAuthenticated && user) {
            // Filter completed games where this user played
            // Note: This is a placeholder - actual implementation needs server-side filtering
            return completedGames.filter(game =>
                game.players.some(p => p.odId === user.id)
            );
        }
        return [];
    }, [activeTab, activeGames, completedGames, isAuthenticated, user]);

    const handleNextPage = (): void => {
        if (hasMore) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePreviousPage = (): void => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const viewReplay = (gameId: string): void => {
        navigate(`/mferchess/replay/${gameId}`);
    };

    const joinGame = (gameId: string): void => {
        navigate(`/mferchess/${gameId}`);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getWinnerColor = (game: CompletedGame): string | null => {
        if (!game.winnerId) return null;
        const winner = game.players.find(p => p.id === game.winnerId);
        return winner ? winner.color : null;
    };

    const getGameResultText = (game: CompletedGame): string => {
        const winnerColor = getWinnerColor(game);
        if (!winnerColor) {
            return 'Draw';
        }
        const displayColor = winnerColor === 'plain' ? 'White' : 'Black';
        return `${displayColor} wins by ${game.endReason}`;
    };

    // Render active games table
    const renderActiveGamesTable = () => (
        <div className="games-table-container">
            <table className="games-table">
                <thead>
                    <tr>
                        <th>Game Name</th>
                        <th>Players</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(filteredGames as ActiveGame[]).map(game => (
                        <tr key={game.id}>
                            <td className="game-name">{game.name}</td>
                            <td className="player-count">{game.playerCount}/2</td>
                            <td>
                                <button
                                    className="join-button"
                                    onClick={() => joinGame(game.id)}
                                >
                                    {game.playerCount < 2 ? 'Join' : 'View'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // Render completed games table
    const renderCompletedGamesTable = () => (
        <>
            <div className="games-table-container">
                <table className="games-table">
                    <thead>
                        <tr>
                            <th>Game Name</th>
                            <th>Result</th>
                            <th>Moves</th>
                            <th>Completed</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(filteredGames as CompletedGame[]).map(game => (
                            <tr key={game.id}>
                                <td className="game-name">{game.name}</td>
                                <td className="game-result">
                                    {getGameResultText(game)}
                                </td>
                                <td className="move-count">{game.moveNumber}</td>
                                <td className="completed-date">
                                    {formatDate(game.completedAt)}
                                </td>
                                <td>
                                    <button
                                        className="replay-button"
                                        onClick={() => viewReplay(game.id)}
                                    >
                                        View Replay
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination only for completed games tab */}
            {activeTab === 'completed' && (
                <div className="pagination-controls">
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="pagination-button"
                    >
                        Previous
                    </button>
                    <span className="page-indicator">
                        Page {currentPage}
                    </span>
                    <button
                        onClick={handleNextPage}
                        disabled={!hasMore}
                        className="pagination-button"
                    >
                        Next
                    </button>
                </div>
            )}
        </>
    );

    return (
        <div className="chess-games-container">
            <h1 className="title">Chess Games</h1>

            <div className="navigation-buttons">
                <button onClick={navigateToLobby} className="nav-button">
                    Create Game
                </button>
                <button onClick={navigateToHome} className="nav-button">
                    Back to Home
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs">
                <button
                    className={`filter-tab ${activeTab === 'active' ? 'active' : ''}`}
                    onClick={() => handleTabChange('active')}
                >
                    Active
                    <span className="tab-count">{activeGames.length}</span>
                </button>
                <button
                    className={`filter-tab ${activeTab === 'completed' ? 'active' : ''}`}
                    onClick={() => handleTabChange('completed')}
                >
                    Completed
                </button>
                {isAuthenticated && (
                    <button
                        className={`filter-tab ${activeTab === 'my-games' ? 'active' : ''}`}
                        onClick={() => handleTabChange('my-games')}
                    >
                        My Games
                    </button>
                )}
            </div>

            {loading && <div className="loading">Loading games...</div>}

            {error && (
                <div className="error">
                    <p>Error: {error}</p>
                    <button onClick={() => fetchGames(currentPage)}>Retry</button>
                </div>
            )}

            {!loading && !error && filteredGames.length === 0 && (
                <p className="no-games">
                    {activeTab === 'active' && 'No active games. Create one!'}
                    {activeTab === 'completed' && 'No completed games found.'}
                    {activeTab === 'my-games' && 'No games found. Play a game first!'}
                </p>
            )}

            {!loading && !error && filteredGames.length > 0 && (
                activeTab === 'active'
                    ? renderActiveGamesTable()
                    : renderCompletedGamesTable()
            )}
        </div>
    );
}

export default ChessGamesView;
